'use client'

import type { EventDatesProps, LinkProps } from '@/types/types'

import React, { useRef } from 'react'
import { slugify } from '@/lib/sanitise-strings'

import RenderComponents from '@/components/flex-content/RenderComponents'
import RenderEventComponents from '@/components/flex-content/RenderEventComponents'

import EventSidebar from './EventSidebar'

import AnchoredContentSidebar from '@/components/anchored-content/AnchoredContentSidebar'
import AnchoredContentBlocks from '@/components/anchored-content/AnchoredContentBlocks'
import EventTerms from './EventTerms'

import TermLinks from '@/components/term-links/TermLinks'

export default function EventContent({
  components,
  dates,
  timezone,
  timezoneModal,
  location,
  formats,
  eventTypes,
  topics,
  hideTopics = false,
  price,
  bookingLink,
  postId,
}: {
  components: any[]
  dates?: EventDatesProps
  timezone?: string
  timezoneModal?: string
  location?: string
  formats?: any[]
  eventTypes?: any[]
  topics?: any[]
  hideTopics?: boolean
  price?: string
  bookingLink?: LinkProps
  postId?: number
}) {
  // Store references to this component and each section
  const componentRef = useRef(null)
  const sectionRefs = useRef([])

  // Bail if no components
  if (!components) {
    return null
  }

  // Based on the component type, generate section titles
  const tabLabels = {
    text: 'Overview',
    google_map: 'Venue',
    contacts: 'Key Contacts',
    people: 'Speakers',
    agenda: 'Agenda',
    logo_grid: 'Sponsors',
    media_partners: 'Media Partners',
    event_partners: 'Event Partners',
    itinerary: 'Itinerary',
  }

  const multipleSections =
    components.filter((component) => {
      return component.acf_fc_layout !== 'title'
    }).length > 1

  // Loop through components and get title/component data
  const anchoredContentSections = components.map((component, index) => {
    const layout = component.acf_fc_layout
    let title = ''

    if (layout !== 'custom_tab') {
      title = tabLabels[layout] || title
      components = [{ ...component }]
    } else {
      title = component?.tab_title || title
      components = component?.accordion_content || []
    }

    return {
      anchorId: slugify(title),
      title: title,
      content: (
        <div className={`${index > 0 ? 'border-t border-gradient pb-m pt-m' : 'pb-m'}`}>
          {title !== 'Overview' ? <span className="h2 mb-0">{title}</span> : null}

          {layout === 'custom_tab' ? (
            <RenderComponents components={components} />
          ) : (
            <RenderEventComponents components={components} />
          )}
        </div>
      ),
    }
  })

  const eventTerms = <EventTerms />

  const showSidebar = dates || timezone || location || eventTypes || price || bookingLink

  return (
    <div ref={componentRef} className="w-full">
      <div className="container px-0 @container">
        <div className="grid w-full grid-cols-[385fr_828fr_minmax(320px,385fr)] items-start gap-xl l:grid-cols-[1fr_2fr] ms:grid-cols-1">
          {multipleSections ? (
            <div className="sticky z-30 flex-grow duration-500 ease-in-out l:order-2 ms:top-[var(--header-height)] ms:breakout ms-up:top-[var(--sticky-offset)] ms-up:max-h-[calc(100vh-var(--sticky-offset))] ms-up:overflow-auto ms-up:pl-[var(--container-padding)]">
              <AnchoredContentSidebar
                sections={anchoredContentSections}
                componentRef={componentRef}
                sectionRefs={sectionRefs}
              />
            </div>
          ) : null}

          <div
            className={`shrink-0 @container/nested-flex l:order-3 ${!multipleSections ? 'ms-up:col-span-2 l-up:pl-[var(--container-padding)]' : ''}`}>
            <div className="mx-auto flex w-[min(var(--container-s-max-w),100%)] flex-shrink-[2] flex-col gap-m @container/main">
              {eventTerms}

              <AnchoredContentBlocks sections={anchoredContentSections} sectionRefs={sectionRefs} />

              {!hideTopics && topics && topics.length ? (
                <div>
                  <TermLinks
                    heading="Topics"
                    terms={topics}
                    postType="all-events"
                    classNames={{
                      item: 'rounded-full bg-grey-50 px-xs py-xxxs hover:bg-black hover:text-white transition-colors duration-500',
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>

          <div className="z-30 w-full flex-grow duration-500 ease-in-out l:order-1 l:col-span-full l-up:sticky l-up:top-[var(--sticky-offset)] l-up:max-h-[calc(100vh-var(--sticky-offset))] l-up:overflow-auto">
            {showSidebar ? (
              <EventSidebar
                eventDates={dates}
                timezone={timezone}
                timezoneModal={timezoneModal}
                location={location}
                formats={formats}
                eventTypes={eventTypes}
                price={price}
                bookingLink={bookingLink}
                postId={postId}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
